import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { connect } from 'redux-bundler-react';
import StepHeader from '@components/step-header/StepHeader';
import ContactFields from '@forms/components/Form/contact-fields/ContactFields';
import SelectInput from '@components/select/Select';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import AgentAuthorizationUpload from '@forms/components/Form/AgentAuthorizationUpload';
import RightOfEntryUpload from '@forms/components/Form/RightOfEntryUpload';
import LinkButton from '@components/link/linkButton';
import Section from '@src/app-components/section/Section';
import _isEqual from 'lodash/isEqual';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import {
  ContactTypes,
  ContactsFormNames,
  ErrorMessages,
  ProfileRoles,
  FileTypes,
  ResourceFiles,
  RequestMap,
} from '@src/utils/enums';
import {
  updateUserProfileAgent,
  userProfileAgentUsed,
  updateAgentsValue,
  getAgentApplicantSelfValidationError,
  hasMatchingApplicantAgent,
} from '@forms/components/modals/_shared/UserProfileAgent';
import { stripUncompletedContacts, formatUSPhoneNumber } from '@src/utils/helpers';
import ContactInput from '@components/contact-input/ContactInput';
import { contactInputValidation } from '@src/utils/validation/inputValidation.contact';
import { useRequest, useContactsTable, useNonPcn } from '@stores';
import { useUserProfile, useResetDirtyFields } from '@hooks';

const agentsToBool = (v) => (v === 'true' ? true : v === 'false' ? false : null);

export const ContactsMetadata = {
  sectionName: 'Contact Information',
};

const baseSubtext =
  'Provide contact information for the applicant and the agent if applicable. The applicant is the individual or entity';
const endSubtext =
  'The agent is a third-party that has been retained by the applicant to act on their behalf in submitting this request to the USACE.';

const isPermitSubtext =
  'If you are an agent you must provide an Agent Authorization Form. Download an Agent Authorization form and upload the completed document in the file upload box below.';

const Contacts = connect(
  'doDownloadFile',
  'doDownloadPopulatedFile',
  'doResetFileSection',
  'doUpdateRequestContacts',
  'doUpdatePreAppRequest',
  'doUpdateJDRequest',
  'doUpdateNprRequest',
  'doUpdatePermitRequest',
  'doUpdateRequestContacts',
  'selectRequestAPIData',
  ({
    doDownloadFile,
    doDownloadPopulatedFile,
    doResetFileSection,
    doUpdateRequestContacts,
    doUpdatePreAppRequest,
    doUpdateJDRequest,
    doUpdateNprRequest,
    doUpdatePermitRequest,
    requestAPIData,
    componentID,
    edit,
    stepNo,
    contactType = ContactTypes.Agent,
    subtext,
    isPermit = false,
    label = 'Agent Contact Info',
    required = true,
  }) => {
    const { userProfileData } = useUserProfile();
    const { setNonPcnRequest } = useNonPcn.getState();
    const { getSelectedRequestId, isFormReadOnly: isReadOnly, isSectionTouched, updateSection } = useRequest();
    const { adjoiningPropOwners, propOwners, saveContactsTable } = useContactsTable();

    const requestType = RequestMap[Number(getSelectedRequestId())];
    const prevRowDataRef = useRef([]);

    const [tableIsDirty, setTableIsDirty] = useState(false);
    const [isUserProfileAgentUsed, setIsUserProfileAgentUsed] = useState(false);
    const [contactRowData, setContactRowData] = useState([]);
    const savedHasHiredAgent = requestAPIData?.request?.[requestType]?.[0]?.hasHiredAgent;
    const savedHasHiredAgentFormValue = typeof savedHasHiredAgent === 'boolean' ? String(savedHasHiredAgent) : null;

    const applicantContact = useMemo(() => {
      const contacts = requestAPIData?.request?.contacts || [];
      return contacts.find((contact) => contact.contactType === ContactTypes.Applicant);
    }, [requestAPIData?.request?.contacts]);

    const contacts = useMemo(() => requestAPIData?.request?.contacts ?? [], [requestAPIData?.request?.contacts]);
    const agentContacts = contacts.filter((contact) => contact.contactType === ContactTypes.Agent);
    const isAgentUser = userProfileData?.role === ProfileRoles.Agent;
    const mustRequireAgent = isAgentUser && !isReadOnly;
    // string value used by the form control
    const hasHiredAgentFormValue = mustRequireAgent ? 'true' : (savedHasHiredAgentFormValue ?? 'false');
    // boolean value used by component logic and request updates
    const hasHiredAgentSelected = mustRequireAgent || savedHasHiredAgent === true;
    const agentsTableLength = contactRowData?.length;

    const schema = useCallback(
      (agentsTableLength) =>
        yup
          .object()
          .shape(
            {
              ...contactInputValidation.nameRequired,
              ...contactInputValidation.addressRequired,
              ...contactInputValidation.phoneRequired,
              ...contactInputValidation.faxOptional,
              ...contactInputValidation.emailRequired,
              agents: yup.string().required(ErrorMessages.SelectOption),
              agentsTable: yup.boolean().when('agents', {
                is: (val) => val === 'true' && agentsTableLength < 1,
                then: () => yup.boolean().required('Agents: At least one agent is required'),
              }),
              agentAuth: yup
                .string()
                .when('agents', {
                  is: 'true',
                  then: () =>
                    yup
                      .string()
                      .required('Please provide an Agent Authorization Form')
                      .test('file-invalid-test', 'File selected is invalid', function (value) {
                        return value !== 'error';
                      }),
                })
                .nullable(),
              agentAuthComments: yup.string().when('agents', { is: 'true', then: () => yup.string().nullable() }),
            },
            [contactInputValidation.faxOptionalDependencies]
          )
          .test('agent-cannot-be-applicant', function (values) {
            const validationError = getAgentApplicantSelfValidationError(userProfileData, values);
            return validationError ? this.createError(validationError) : true;
          })
          .test('applicant-must-not-match-agent', function (values) {
            if (values?.agents !== 'true') return true;

            const matchesAgent = hasMatchingApplicantAgent(contactRowData, {
              contactType: ContactTypes.Applicant,
              firstName: values?.firstName,
              lastName: values?.lastName,
              emailAddress: values?.emailAddress,
            });

            return matchesAgent
              ? this.createError({
                  path: 'firstName',
                  message: 'The Applicant cannot also be listed as an Agent',
                })
              : true;
          }),
      [contactRowData, userProfileData]
    );

    const defaultValues = useMemo(
      () => ({
        salutation: applicantContact?.salutation ?? '',
        firstName: applicantContact?.firstName ?? '',
        middleName: applicantContact?.middleName ?? '',
        lastName: applicantContact?.lastName ?? '',
        address: applicantContact?.address ?? '',
        addressTwo: applicantContact?.addressTwo ?? '',
        city: applicantContact?.city ?? '',
        state: applicantContact?.state ?? '',
        zipcode: applicantContact?.zipcode ?? '',
        country: applicantContact?.country ?? 'US',
        phoneOneType: applicantContact?.phoneOneType ?? '',
        phoneOneCountryCode: applicantContact?.phoneOneCountryCode ?? '1',
        phoneOne: applicantContact?.phoneOne
          ? applicantContact?.phoneOneCountryCode === '1'
            ? formatUSPhoneNumber(applicantContact.phoneOne)
            : applicantContact?.phoneOne
          : '',
        phoneOneExtension: applicantContact?.phoneOneExtension ?? '',
        phoneTwoType: applicantContact?.phoneTwoType ?? '',
        phoneTwoCountryCode: applicantContact?.phoneTwoCountryCode ?? '1',
        phoneTwo: applicantContact?.phoneTwo
          ? applicantContact?.phoneTwoCountryCode === '1'
            ? formatUSPhoneNumber(applicantContact.phoneTwo)
            : applicantContact?.phoneTwo
          : '',
        phoneTwoExtension: applicantContact?.phoneTwoExtension ?? '',
        faxCountryCode: applicantContact?.faxCountryCode ?? '1',
        faxPhone: applicantContact?.faxPhone
          ? applicantContact?.faxCountryCode === '1'
            ? formatUSPhoneNumber(applicantContact.faxPhone)
            : applicantContact?.faxPhone
          : '',
        emailAddress: applicantContact?.emailAddress ?? '',
        company: applicantContact?.company ?? '',
        agents: hasHiredAgentFormValue,
        agentAuthComments: requestAPIData?.request?.agentAuthComments ?? '',
        ownerEntryComments: requestAPIData?.request?.ownerEntryComments ?? '',
      }),
      [
        applicantContact,
        hasHiredAgentFormValue,
        requestAPIData?.request?.agentAuthComments,
        requestAPIData?.request?.ownerEntryComments,
      ]
    );

    const methods = useForm({
      resolver: yupResolver(schema(agentsTableLength)),
      mode: 'onBlur',
      defaultValues,
    });
    const {
      formState: { errors, dirtyFields, isValid },
      watch,
      trigger,
      setError,
      clearErrors,
      getFieldState,
      setValue,
      reset,
    } = methods;
    const agents = watch('agents');
    const firstName = watch('firstName');
    const lastName = watch('lastName');
    const emailAddress = watch('emailAddress');
    const isDirty = Object.values(dirtyFields).length > 0;
    const isTouched = isSectionTouched(stepNo);

    const formValues = watch();
    const contactValues = useMemo(() => {
      return {
        address: formValues.address,
        addressTwo: formValues.addressTwo,
        city: formValues.city,
        company: formValues.company,
        contactID: formValues?.sharedContact
          ? undefined
          : (formValues?.contactID ?? applicantContact?.contactID ?? undefined),
        contactType: ContactTypes.Applicant,
        country: formValues.country,
        createdBy: formValues?.createdBy ?? undefined,
        emailAddress: formValues.emailAddress,
        faxCountryCode: formValues.faxCountryCode,
        faxPhone: formValues?.faxPhone?.replace(/\D/g, '') ?? undefined,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        middleName: formValues.middleName,
        ownerEntryComments: formValues.ownerEntryComments,
        phoneOne: formValues?.phoneOne?.replace(/\D/g, '') ?? undefined,
        phoneOneCountryCode: formValues.phoneOneCountryCode,
        phoneOneExtension: formValues.phoneOneExtension,
        phoneOneType: formValues.phoneOneType,
        phoneTwo: formValues?.phoneTwo?.replace(/\D/g, '') ?? undefined,
        phoneTwoCountryCode: formValues.phoneTwoCountryCode,
        phoneTwoExtension: formValues.phoneTwoExtension,
        phoneTwoType: formValues.phoneTwoType,
        requestID: formValues?.requestID ?? undefined,
        rightOfEntryFile: formValues.rightOfEntryFile,
        salutation: formValues.salutation,
        state: formValues.state,
        version: formValues?.version ?? undefined,
        zipcode: formValues.zipcode,
      };
    }, [
      formValues.address,
      formValues.addressTwo,
      formValues.city,
      formValues.company,
      formValues.country,
      formValues.emailAddress,
      formValues.faxCountryCode,
      formValues.faxPhone,
      formValues.firstName,
      formValues.lastName,
      formValues.middleName,
      formValues.ownerEntryComments,
      formValues.phoneOne,
      formValues.phoneOneCountryCode,
      formValues.phoneOneExtension,
      formValues.phoneOneType,
      formValues.phoneTwo,
      formValues.phoneTwoCountryCode,
      formValues.phoneTwoExtension,
      formValues.phoneTwoType,
      formValues.rightOfEntryFile,
      formValues.salutation,
      formValues.state,
      formValues.zipcode,
      formValues.sharedContact,
      formValues.contactID,
      formValues.requestID,
      formValues.version,
      formValues.createdBy,
      applicantContact?.contactID,
    ]);

    useEffect(() => {
      trigger();
    }, [agents, contactRowData?.length, trigger]);

    const updateRequestHasHiredAgent = useCallback(
      (value) => {
        const hasHiredAgentValue = typeof value === 'boolean' ? value : agentsToBool(value);
        if (hasHiredAgentValue === null) return;
        const payload = { hasHiredAgent: hasHiredAgentValue };
        switch (requestType) {
          case 'permits':
            return doUpdatePermitRequest(payload);
          case 'preApplications':
            return doUpdatePreAppRequest(payload);
          case 'jdRequests':
            return doUpdateJDRequest(payload);
          case 'noPermitRequireds':
            return doUpdateNprRequest(payload);
          case 'nonPCNRequests':
            return setNonPcnRequest({
              ...requestAPIData?.request?.nonPCNRequests?.[0],
              hasHiredAgent: hasHiredAgentValue,
            });
          default:
            return;
        }
      },
      [
        requestType,
        requestAPIData,
        doUpdatePermitRequest,
        doUpdatePreAppRequest,
        doUpdateJDRequest,
        doUpdateNprRequest,
        setNonPcnRequest,
      ]
    );

    useEffect(() => {
      setValue('agents', hasHiredAgentFormValue, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      if (savedHasHiredAgent !== hasHiredAgentFormValue) {
        updateRequestHasHiredAgent(hasHiredAgentSelected);
      }
    }, [setValue, hasHiredAgentFormValue, savedHasHiredAgent, hasHiredAgentSelected, updateRequestHasHiredAgent]);

    useEffect(() => {
      const tableDataChanged = !_isEqual(contactRowData, prevRowDataRef.current);
      tableDataChanged && setTableIsDirty(true);
    }, [contactRowData]);

    //Reset the dirty states of the fields after a save.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    useEffect(() => {
      setIsUserProfileAgentUsed(
        userProfileAgentUsed(
          contactRowData,
          userProfileData?.firstName,
          userProfileData?.lastName,
          userProfileData?.emailAddress
        )
      );
    }, [contactRowData, userProfileData]);

    useEffect(() => {
      // Load tables from database
      let initialRowData = requestAPIData?.request?.contacts?.filter(
        (contact) => contact.contactType === ContactTypes.Agent
      );
      // If country is null, default to US
      initialRowData = initialRowData?.map((contact) =>
        contact.country === null ? { ...contact, country: 'US' } : contact
      );
      if (initialRowData) {
        //Update agents array if user profile agent exists
        if (
          userProfileAgentUsed(
            initialRowData,
            userProfileData?.firstName,
            userProfileData?.lastName,
            userProfileData?.emailAddress
          )
        ) {
          initialRowData = updateUserProfileAgent(initialRowData, userProfileData);
        }
        updateAgentsValue('agent', initialRowData, setValue);
        setValue('agents', hasHiredAgentFormValue, { shouldDirty: false, shouldValidate: false });

        setContactRowData(initialRowData);
        setTableIsDirty(false);
        prevRowDataRef.current = initialRowData;
        isTouched && trigger();
      }
    }, [requestAPIData, isTouched, userProfileData, setValue, trigger, hasHiredAgentFormValue]);

    useEffect(() => {
      const contacts = [contactValues, ...contactRowData, ...propOwners, ...adjoiningPropOwners];
      doUpdateRequestContacts(stripUncompletedContacts(contacts));
    }, [contactValues, doUpdateRequestContacts, applicantContact, contactRowData, propOwners, adjoiningPropOwners]);

    useEffect(() => {
      reset(defaultValues, {
        keepDirty: true,
        keepErrors: true,
      });
    }, [defaultValues, reset]);

    useEffect(() => {
      if (!hasHiredAgentSelected) {
        setContactRowData([]);
        prevRowDataRef.current = [];
        setTableIsDirty(false);
        setIsUserProfileAgentUsed(false);
        doResetFileSection(ContactsFormNames.AgentAuthorization, true);
        doResetFileSection(ContactsFormNames.RightOfEntry, true);
        isTouched && trigger(['agentsTable', 'agentAuth']);
      }
    }, [hasHiredAgentSelected, doResetFileSection, isTouched, trigger]);

    useEffect(() => {
      if (isTouched) {
        trigger(['agentsTable', 'agentAuth']);
      }
    }, [agents, contactRowData?.length, isTouched, trigger]);

    useEffect(() => {
      contactRowData && saveContactsTable('agents', contactRowData);
    }, [contactRowData, saveContactsTable]);

    useEffect(() => {
      const matchesAgent =
        agents === 'true' &&
        hasMatchingApplicantAgent(contactRowData, {
          contactType: ContactTypes.Applicant,
          firstName,
          lastName,
          emailAddress,
        });

      const currentError = getFieldState('firstName').error;
      const restrictedMessages = [
        'You cannot add yourself as an applicant',
        'The Applicant cannot also be listed as an Agent',
      ];

      if (matchesAgent) {
        setError('firstName', {
          type: 'manual',
          message: 'The Applicant cannot also be listed as an Agent',
        });
        return;
      }

      if (currentError?.type === 'manual' && restrictedMessages.includes(currentError.message)) {
        clearErrors('firstName');
      }

      trigger('firstName');
    }, [agents, contactRowData, firstName, lastName, emailAddress, setError, clearErrors, getFieldState, trigger]);

    const handleAgentsChange = useCallback(
      (e) => {
        const value = e.target.value;
        setValue('agents', value, { shouldDirty: true, shouldValidate: true });
        updateRequestHasHiredAgent(value);
      },
      [setValue, updateRequestHasHiredAgent]
    );

    useEffect(() => {
      const dirty = tableIsDirty || isDirty;
      updateSection(stepNo, {
        dirty,
        valid: isReadOnly ? true : isValid,
        label: ContactsMetadata.sectionName,
      });
    }, [stepNo, isDirty, isValid, tableIsDirty, isReadOnly, updateSection]);

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader
          text='Contact Information'
          subtext={`${baseSubtext} ${subtext} ${endSubtext} ${isPermit ? isPermitSubtext : ''}`}
        />
        {/* Contact Section comes first when role is not agent  */}
        {userProfileData?.role !== ProfileRoles.Agent && (
          <ContactFields
            type={ContactTypes.Applicant}
            label={ContactTypes.Applicant}
            edit={edit}
            isReadOnly={isReadOnly}
            showButton={!isReadOnly}
          />
        )}

        <Section label='Agents'>
          <div id='agentsTable'>
            <SelectInput
              className='width-50'
              fullWidth={false}
              name='agents'
              label='Has the applicant hired an agent to complete the application process?'
              required
              readOnly={isReadOnly || isAgentUser}
              onChange={handleAgentsChange}
            >
              <option key='2' value='true'>
                Yes
              </option>
              <option key='3' value='false'>
                No
              </option>
            </SelectInput>
          </div>

          {agents === 'true' && (
            <>
              <ContactInput
                contactType={contactType}
                initialContacts={agentContacts}
                label={label}
                name={'contacts'}
                required={required}
                onContactRowDataChange={setContactRowData}
                userProfileData={userProfileData}
                agentProfile={isUserProfileAgentUsed}
                isReadOnly={isReadOnly}
                contacts={contactRowData}
              />
              <AgentAuthorizationUpload componentID={componentID} />

              {/* Applies to permit request types */}
              {isPermit && (
                <RightOfEntryUpload
                  componentID={componentID}
                  subText={
                    <>
                      <p>
                        Note: If a site visit is needed, you, as the agent must also provide a Right of Entry form. This
                        form ensures that USACE has the legal right to enter the property.{' '}
                      </p>
                      <p>
                        <LinkButton
                          onClick={() => doDownloadFile(ResourceFiles.Eng6294, FileTypes.Resource)}
                          title='Download a blank Right of Entry form'
                          content='Download a blank Right of Entry form'
                        />{' '}
                        to complete or{' '}
                        <LinkButton
                          onClick={() => doDownloadPopulatedFile(ContactsFormNames.RightOfEntry, ResourceFiles.Eng6294)}
                          title='download a Right of Entry form populated'
                          content='download a Right of Entry form populated'
                        />{' '}
                        with the information you entered. Upload the completed document in the section below.
                      </p>
                    </>
                  }
                />
              )}

              {/* Contact Section comes after Agent Section when role is agent */}
              {userProfileData?.role === ProfileRoles.Agent && (
                <ContactFields
                  type={ContactTypes.Applicant}
                  label={ContactTypes.Applicant}
                  edit={edit}
                  isReadOnly={isReadOnly}
                  showButton={false}
                />
              )}
            </>
          )}
        </Section>
      </FormProvider>
    );
  }
);
Contacts.metadata = ContactsMetadata;

export default Contacts;
